import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  PiArchive,
  PiArrowCounterClockwise,
  PiCalendar,
  PiCheckCircle,
  PiEnvelope,
  PiEye,
  PiGear,
  PiGlobe,
  PiPause,
  PiPencilSimple,
  PiPlay,
  PiUser,
  PiUserPlus,
} from 'react-icons/pi'
import { Spinner } from 'react-bootstrap'

import { cn } from 'components/UI/cn'
import { NotFoundContent } from '../not-found-content'
import { WORKFLOW_STATUS } from 'models/campaign'
import { useGetCampaignSequence } from 'features/campaigns/sequences_api'
import {
  usePauseCampaign,
  useResumeCampaign,
  useGetCampaignSettings,
} from 'services/campaigns'
import { useGetReplyThreadSummary } from 'features/campaigns/replies_api'
import {
  useArchiveCampaign,
  useGetCampaignById,
  useUnarchiveCampaign,
} from 'features/campaigns/campaigns_api'
import { ArchiveCampaignModal } from 'features/campaigns/ArchiveCampaignModal'
import { EditCampaignModalV2 } from 'features/campaigns/EditCampaignModalV2'
import { CampaignDashboardOverviewTab } from 'features/campaigns/CampaignDashboardOverviewTab'
import { CampaignDashboardTargetsTab } from 'features/campaigns/CampaignDashboardTargetsTab'
import { CampaignDashboardContactsTab } from 'features/campaigns/CampaignDashboardContactsTab'
import { useInvalidateCampaignContactRequests } from 'services/contactRequest'
import { CampaignDashboardDealsTab } from 'features/campaigns/CampaignDashboardDealsTab'
import { CampaignRepliesTab } from 'features/campaigns/CampaignRepliesTab'
import { useEntityBreadcrumbs } from 'context/layout'
import FbButton from 'components/FbUI/FbButton'
import { Ds2026ButtonProvider } from 'components/FbUI/Ds2026ButtonContext'
import { NoticeModal } from 'components/FbUI/NoticeModal'
import {
  ActionsDropdown,
  type ActionItem,
} from 'components/Buttons/ActionsDropdown'
import { ds2026DropdownTriggerButtonClass } from 'components/FbUI/ds2026DropdownMenu'
import { ContactRequestJobFromCampaignModal } from 'components/Modals/ContactRequestJobFromCampaignModal'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from 'components/UI/Modal/ModalPrimitive'
import { TargetArrowIcon } from 'components/Layout/LeftNav/nav-icons'
import { ALLOWED_CONTACT_REQUEST_TYPES } from 'models/companies'
import { FBPage } from 'components/FbUI/Page/FBPage'
import {
  FBPageHeader,
  FBPageTitleRow,
  FBPageTitle,
  FBPageInnerTabsContainer,
} from 'components/FbUI/Page/styles'
import { UnderlineTabs } from 'components/FbUI/UnderlineTabs'

const DASHBOARD_TAB_KEYS = [
  'overview',
  'targets',
  'contacts',
  'deals',
  'replies',
] as const
type DashboardTab = (typeof DASHBOARD_TAB_KEYS)[number]

function isDashboardTab(value: string | null): value is DashboardTab {
  return (
    value !== null && (DASHBOARD_TAB_KEYS as readonly string[]).includes(value)
  )
}

function getStatusBadge(status: string): {
  label: string
  className: string
} {
  switch (status) {
    case WORKFLOW_STATUS.DRAFT:
    case 'draft':
      return {
        label: 'Draft',
        className: 'bg-gray-100 text-gray-700',
      }
    case WORKFLOW_STATUS.ACTIVE:
      return {
        label: 'Active',
        className: 'bg-success-bg-subtle text-success-strong',
      }
    case WORKFLOW_STATUS.PAUSED:
      return {
        label: 'Paused',
        className: 'bg-warning-bg-subtle text-warning-strong',
      }
    case WORKFLOW_STATUS.COMPLETE:
    case 'completed':
      return {
        label: 'Complete',
        className: 'bg-success-bg-subtle text-success-strong',
      }
    case 'archived':
      return {
        label: 'Archived',
        className: 'bg-neutral-100 text-neutral-700',
      }
    case 'deleted':
      return {
        label: 'Deleted',
        className: 'bg-neutral-100 text-neutral-700',
      }
    case 'launching':
      return {
        label: 'Launching',
        className: 'bg-info-bg-subtle text-info-strong',
      }
    case 'queued':
      return {
        label: 'Queued',
        className: 'bg-info-bg-subtle text-info-strong',
      }
    case 'failed':
      return {
        label: 'Failed',
        className: 'bg-error-bg-subtle text-error-strong',
      }
    case 'pending deletion':
      return {
        label: 'Pending Deletion',
        className: 'bg-error-bg-subtle text-error-strong',
      }
    default:
      return {
        label: status,
        className: 'bg-neutral-100 text-fg-secondary',
      }
  }
}

export function CampaignDashboardPageV2() {
  const params = useParams<{ id: string }>()
  const navigate = useNavigate()
  const campaignId = Number(params.id)

  const { data: campaign, isLoading: campaignsLoading } =
    useGetCampaignById(campaignId)

  const breadcrumbs = useMemo(
    () => (campaign?.name ? [{ label: campaign.name }] : []),
    [campaign?.name]
  )
  useEntityBreadcrumbs(breadcrumbs)

  const { data: sequence } = useGetCampaignSequence(campaignId, !!campaign)
  const bisonCampaignId = sequence?.bison_campaign_id ?? 0

  const { data: replySummary } = useGetReplyThreadSummary({
    campaign_id: bisonCampaignId,
  })
  const { data: settings } = useGetCampaignSettings(campaignId, !!campaign)

  const pauseMutation = usePauseCampaign()
  const resumeMutation = useResumeCampaign()
  const handleContactRequested =
    useInvalidateCampaignContactRequests(campaignId)

  const [searchParams] = useSearchParams()
  // Read ?tab= once on mount so subsequent in-app tab clicks aren't
  // overridden by a stale URL. The ?thread= param is owned by
  // CampaignRepliesTab itself (it also clears it when the user dismisses
  // the linked-thread filter).
  const initialTab = useMemo<DashboardTab>(() => {
    const t = searchParams.get('tab')
    return isDashboardTab(t) ? t : 'overview'
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const [activeTab, setActiveTab] = useState<DashboardTab>(initialTab)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false)
  const [showContactRequestModal, setShowContactRequestModal] = useState(false)
  const [showNoTargetsNotice, setShowNoTargetsNotice] = useState(false)
  const archiveMutation = useArchiveCampaign()
  const unarchiveMutation = useUnarchiveCampaign()

  if (campaignsLoading) {
    return (
      <div className="flex flex-1 items-center justify-center py-20">
        <Spinner animation="border" />
      </div>
    )
  }

  if (!campaign) {
    return <NotFoundContent />
  }

  const campaignStatus = settings?.status || campaign.workflow_status
  const isPaused = campaignStatus === WORKFLOW_STATUS.PAUSED
  const isComplete =
    campaign.is_archived ||
    campaignStatus === 'completed' ||
    campaignStatus === 'archived' ||
    campaignStatus === 'deleted' ||
    campaignStatus === WORKFLOW_STATUS.COMPLETE
  const canControl = !isComplete
  const targetCount = campaign.contact_company_count ?? 0
  const hasContactRequests = !campaign.type
    ? true
    : ALLOWED_CONTACT_REQUEST_TYPES.includes(campaign.type.slug as any)
  const badge = getStatusBadge(campaignStatus)
  const emailStepsCount = sequence?.steps?.length ?? 0
  const isActiveCampaign =
    campaignStatus === 'active' ||
    campaignStatus === 'queued' ||
    campaignStatus === 'launching'
  const sequenceButtonLabel =
    isComplete || isActiveCampaign ? 'View Sequence' : 'Edit Sequence'
  // Request Contacts lives in the Actions menu for running campaigns (active /
  // paused), not completed ones.
  const showRequestContactsMenuItem =
    (isActiveCampaign || isPaused) && hasContactRequests

  const actions: ActionItem[] = [
    {
      label: 'Edit Campaign',
      icon: PiGear,
      onClick: () => setShowEditModal(true),
    },
    ...(showRequestContactsMenuItem
      ? [
          {
            label: 'Request Contacts',
            icon: PiUserPlus,
            onClick: () => {
              if (targetCount === 0) {
                setShowNoTargetsNotice(true)
              } else {
                setShowContactRequestModal(true)
              }
            },
          },
        ]
      : []),
    campaign.is_archived
      ? {
          label: 'Unarchive Campaign',
          icon: PiArrowCounterClockwise,
          onClick: () => unarchiveMutation.mutate(campaignId),
          disabled: unarchiveMutation.isPending,
        }
      : {
          label: 'Archive Campaign',
          icon: PiArchive,
          onClick: () => setShowArchiveConfirm(true),
        },
  ]

  const tabCounts = {
    targets: campaign.contact_company_count ?? 0,
    contacts: campaign.contact_count ?? 0,
    deals: campaign.deal_count ?? 0,
    replies: replySummary?.is_available ? (replySummary?.total ?? 0) : 0,
  }

  const dashboardTabs: {
    key: DashboardTab
    title: string
    count?: number | null
  }[] = [
    { key: 'overview', title: 'Overview' },
    { key: 'targets', title: 'Targets', count: tabCounts.targets },
    { key: 'contacts', title: 'Contacts', count: tabCounts.contacts },
    { key: 'deals', title: 'Deals', count: tabCounts.deals },
    { key: 'replies', title: 'Replies', count: tabCounts.replies },
  ]

  const handlePauseResume = () => {
    const onError = () =>
      toast.error(`Failed to ${isPaused ? 'resume' : 'pause'} campaign`)
    if (isPaused) {
      resumeMutation.mutate(
        { bison_campaign_id: bisonCampaignId, campaign_id: campaignId },
        { onError }
      )
    } else {
      pauseMutation.mutate(
        { bison_campaign_id: bisonCampaignId, campaign_id: campaignId },
        { onError }
      )
    }
  }

  return (
    <>
      {isComplete && (
        <div
          className={cn(
            'border-b',
            campaign.is_archived
              ? 'border-alert-default-border bg-alert-default-bg'
              : 'border-alert-success-border bg-alert-success-bg'
          )}
        >
          <div className="flex items-center py-3 pl-[calc(var(--page-vertical-padding)+var(--page-container-horizontal-padding))] pr-[var(--page-vertical-padding)]">
            <div
              className={cn(
                'flex items-center gap-2 text-sm font-medium tracking-[-0.2px]',
                campaign.is_archived
                  ? 'text-alert-default-title'
                  : 'text-alert-success-title'
              )}
            >
              {campaign.is_archived ? (
                <PiArchive className="size-4" />
              ) : (
                <PiCheckCircle className="size-4" />
              )}
              {campaign.is_archived
                ? 'This campaign has been archived.'
                : 'This campaign has been completed.'}
            </div>
          </div>
        </div>
      )}

      <FBPage
        innerPageClassName="!bg-surface-page"
        containerClassName="!bg-surface-page !shadow-none"
      >
        <FBPageHeader>
          <div className="flex flex-col items-start gap-2">
            <span
              className={cn(
                'inline-flex items-center rounded-2xl border-0 px-2.5 py-0.5 text-sm font-medium leading-5 tracking-[-0.2px]',
                badge.className
              )}
              style={
                campaignStatus === 'draft'
                  ? { backgroundColor: '#f3f4f6', color: '#374151' }
                  : undefined
              }
            >
              {badge.label}
            </span>
            <FBPageTitleRow className="w-auto">
              <FBPageTitle>
                {campaign.name}
                {campaign.type && (
                  <span className="ml-2 align-middle text-base font-normal text-gray-500">
                    · {campaign.type.name}
                  </span>
                )}
              </FBPageTitle>
            </FBPageTitleRow>
          </div>
          <Ds2026ButtonProvider>
            <div className="flex items-center gap-2">
              <FbButton
                onClick={() => navigate(`/campaigns/${campaignId}/sequence`)}
                variant={canControl ? 'secondary' : 'primary'}
                className="whitespace-nowrap"
              >
                {isComplete || isActiveCampaign ? (
                  <PiEye className="size-4" />
                ) : (
                  <PiPencilSimple className="size-4" />
                )}
                <span>{sequenceButtonLabel}</span>
              </FbButton>
              {canControl && (
                <FbButton
                  onClick={handlePauseResume}
                  disabled={pauseMutation.isPending || resumeMutation.isPending}
                  variant="primary"
                  className="whitespace-nowrap"
                >
                  {isPaused ? (
                    <PiPlay className="size-4" />
                  ) : (
                    <PiPause className="size-4" />
                  )}
                  <span>{isPaused ? 'Resume' : 'Pause'}</span>
                </FbButton>
              )}

              <ActionsDropdown
                actions={actions}
                orientation="horizontal"
                triggerClassName={ds2026DropdownTriggerButtonClass}
              />
            </div>
          </Ds2026ButtonProvider>
        </FBPageHeader>

        <div className="flex flex-wrap items-center gap-6 mb-4">
          {campaign.launch_date && (
            <span className="flex items-center gap-1.5 text-sm text-gray-500 tracking-[-0.2px]">
              <PiCalendar className="size-4 text-brand-active" />
              Projected:{' '}
              <span className="font-medium text-gray-900">
                {new Date(campaign.launch_date).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
            </span>
          )}

          <span className="flex items-center gap-1.5 text-sm text-gray-500 tracking-[-0.2px]">
            <PiGlobe className="size-4 text-brand-active" />
            Sequence Name:{' '}
            <span className="font-medium text-gray-900">
              {settings?.name ?? '—'}
            </span>
          </span>

          <span className="flex items-center gap-1.5 text-sm text-gray-500 tracking-[-0.2px]">
            <PiEnvelope className="size-4 text-brand-active" />
            Email Steps:{' '}
            <span className="font-medium text-gray-900">{emailStepsCount}</span>
          </span>

          {campaign.owner_name && (
            <span className="flex items-center gap-1.5 text-sm text-gray-500 tracking-[-0.2px]">
              <PiUser className="size-4 text-brand-active" />
              Owner:{' '}
              <span className="font-medium text-gray-900">
                {campaign.owner_name}
              </span>
            </span>
          )}
        </div>

        <FBPageInnerTabsContainer>
          <UnderlineTabs<DashboardTab>
            tabs={dashboardTabs}
            activeKey={activeTab}
            onChange={setActiveTab}
            idPrefix="campaign-dashboard"
            aria-label="Campaign sections"
          />
        </FBPageInnerTabsContainer>

        <div className="flex flex-col flex-1 min-h-0">
          {activeTab === 'overview' && (
            <CampaignDashboardOverviewTab
              campaign={campaign}
              campaignId={campaignId}
              campaignStatus={campaignStatus}
              onTabChange={(tab) => setActiveTab(tab as DashboardTab)}
            />
          )}
          {activeTab === 'targets' && (
            <CampaignDashboardTargetsTab campaign={campaign} />
          )}
          {activeTab === 'contacts' && (
            <CampaignDashboardContactsTab
              campaign={campaign}
              onSwitchToTargets={() => setActiveTab('targets')}
              onContactRequested={handleContactRequested}
            />
          )}
          {activeTab === 'deals' && (
            <CampaignDashboardDealsTab campaign={campaign} />
          )}
          {activeTab === 'replies' && (
            <CampaignRepliesTab campaignId={Number(campaignId)} />
          )}
        </div>
      </FBPage>

      <EditCampaignModalV2
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        campaign={campaign}
        campaignId={campaign.id}
      />
      <ArchiveCampaignModal
        show={showArchiveConfirm}
        onHide={() => setShowArchiveConfirm(false)}
        campaignName={campaign.name}
        isActive={campaignStatus === WORKFLOW_STATUS.ACTIVE}
        onConfirm={() => {
          archiveMutation.mutate(campaignId, {
            onSuccess: () => navigate('/campaigns'),
          })
        }}
        isLoading={archiveMutation.isPending}
      />
      <ContactRequestJobFromCampaignModal
        campaignId={campaign.id}
        isMultiType={!campaign.type}
        externalOpen={showContactRequestModal}
        onExternalClose={() => setShowContactRequestModal(false)}
        onContactRequested={handleContactRequested}
      />
      <Dialog open={showNoTargetsNotice} onOpenChange={setShowNoTargetsNotice}>
        <DialogContent
          noPadding
          disableCloseIcon
          aria-describedby={undefined}
          className="!w-auto !max-w-none !overflow-visible !border-0 !bg-transparent !py-0 !shadow-none"
        >
          <DialogTitle className="sr-only">Add targets first</DialogTitle>
          <NoticeModal
            icon={TargetArrowIcon}
            title="Add targets first"
            description="Request Contacts pulls contacts for the companies in your campaign. Add a few targets, then come back to request."
            onClose={() => setShowNoTargetsNotice(false)}
            actions={[
              {
                label: 'Cancel',
                variant: 'secondary',
                onClick: () => setShowNoTargetsNotice(false),
              },
              {
                label: 'Add Targets',
                variant: 'primary',
                icon: TargetArrowIcon,
                onClick: () => {
                  setShowNoTargetsNotice(false)
                  setActiveTab('targets')
                },
              },
            ]}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
